import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { RiCloseLine } from "react-icons/ri";
import Button from "../../../Utility/Button/Button";
import Typography from "../../../Utility/Typography";
import FormSubmitButton from "../../../Utility/Button/FormSubmitButton";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  'button:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function getFocusables(root) {
  if (!root) return [];
  return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (!(el instanceof HTMLElement)) return false;
    if (el.getAttribute("aria-hidden") === "true") return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === "hidden" || style.display === "none") return false;
    return true;
  });
}

/**
 * Rendered via {@link createPortal} to `document.body` so the dimmed backdrop covers the full app
 * (sidebar, header, Kanban `isolate` / DnD stacking) — same pattern as {@link BacklogEditModal}.
 */
const CreateModal = ({ open, onClose, children, submitDisabled = false }) => {
  const titleId = useId();
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key !== "Tab") return;

      const root = wrapperRef.current;
      if (!root) return;
      const list = getFocusables(root);
      if (list.length === 0) return;

      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;

      if (active == null || !root.contains(active)) {
        e.preventDefault();
        first.focus();
        return;
      }

      if (e.shiftKey) {
        if (active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    const focusId = window.setTimeout(() => {
      const root = wrapperRef.current;
      if (!root) return;
      const list = getFocusables(root);
      (list[0] ?? root).focus?.();
    }, 0);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      window.clearTimeout(focusId);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={wrapperRef}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />

      <div className="relative w-[720px] max-w-[95%] h-[85vh] bg-white rounded-xl shadow-2xl flex flex-col z-[1]">
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <Typography
            id={titleId}
            variant="h5"
            component="h2"
            className="font-semibold text-text-primary"
          >
            Create Task
          </Typography>
          <Button
            variant="outline"
            size="sm"
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RiCloseLine className="w-5 h-5" aria-hidden />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">{children}</div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t shrink-0 bg-white">
          <Button type="button" variant="danger" size="md" onClick={onClose} className="btn-reset">
            Cancel
          </Button>
          <FormSubmitButton
            type="submit"
            form="create-task-form"
            loading={submitDisabled}
            label="Create"
            loadingText="Creating..."
            idleIconClass=""
            className="inline-flex items-center gap-2"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateModal;
